import { ExternalLink } from 'lucide-react';
import { NavLink } from 'react-router-dom';

import { Badge, type BadgeTone } from '@/components/ui/badge';
import { NavIcon } from '@/lib/icons';
import type { NavItem } from '@/lib/types';
import { cn } from '@/lib/utils';

export function badgeTone(variant: string): BadgeTone {
  const map: Record<string, BadgeTone> = {
    primary: 'primary',
    success: 'success',
    warning: 'warning',
    danger: 'danger',
    info: 'info',
  };
  return map[variant] ?? 'primary';
}

/**
 * One labelled navigation row: the full sidebar, the mobile drawer, and the
 * rail flyouts all render items through this so they cannot drift apart.
 */
export function NavRow({
  item,
  count,
  className,
}: {
  item: NavItem;
  /** Live count from the ops index, preferred over the boot-time badge. */
  count?: number;
  className?: string;
}) {
  const value = count ?? item.badge ?? 0;
  const badge =
    value > 0 ? (
      <Badge tone={badgeTone(item.badge_variant)} size="xs" className="ml-auto tabular-nums">
        {value > 99 ? '99+' : value}
      </Badge>
    ) : null;

  const body = (
    <>
      <NavIcon name={item.icon} className="size-4 shrink-0" />
      <span className="truncate">{item.title}</span>
      {badge}
    </>
  );

  const rowClasses = (active: boolean) =>
    cn(
      'group relative flex items-center gap-2.5 rounded-md py-1.5 pl-2.5 pr-2 text-[13px] transition-colors',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
      active
        ? 'bg-primary-soft font-medium text-primary'
        : 'text-ink-2 hover:bg-surface-2 hover:text-ink',
      className,
    );

  // Items that Unfold points at a non-console Django page keep working by
  // linking out, so nothing in the sidebar dead-ends during the migration.
  if (!item.route) {
    return (
      <a href={item.external_url ?? '#'} className={rowClasses(false)}>
        {body}
        <ExternalLink className="size-3 shrink-0 text-ink-4 opacity-0 transition-opacity group-hover:opacity-100" />
      </a>
    );
  }

  return (
    <NavLink
      to={item.route}
      end={item.route === '/'}
      className={({ isActive }) =>
        cn(
          rowClasses(isActive),
          // Active pill carries a 2px indicator bar on its leading edge.
          isActive &&
            'before:absolute before:inset-y-1 before:left-0 before:w-0.5 before:rounded-full before:bg-primary',
        )
      }
    >
      {body}
    </NavLink>
  );
}
